import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { PTreeNodeInterface } from '@modules/tree/node/p-tree-node.interface';

@Injectable({ providedIn: 'root' })
export class PTreeObservableService {
    private renameSubject = new Subject<{ nodeId: number; newName: string }>();
    private deletedSubject = new Subject<number>();
    private statusChangedSubject = new Subject<{ nodeId: number; newStatus: string }>();
    private stateChangedSubject = new Subject<{ nodeId: number; stateKey; stateValue }>();
    private treeReadySubject = new Subject<{ type: TREE_TYPE; tree }>();
    private actionCompletedSubject = new Subject();
    private nodeRenamedByTreeSubject = new Subject<{ nodeId: number; newName: string }>();
    private lastSelectedNodeSubject = new BehaviorSubject<PTreeNodeInterface | null>(null);

    private refreshTreeSubject = new Subject<void>();
    public refreshTree$ = this.refreshTreeSubject.asObservable();

    private refreshCyclesSubject = new Subject<void>();
    public refreshCycle$ = this.refreshCyclesSubject.asObservable();

    private refreshNodeSubject = new Subject<void>();
    public refreshSelectedNode$ = this.refreshNodeSubject.asObservable();

    public triggerRefresh(): void {
        this.refreshTreeSubject.next();
        this.refreshCyclesSubject.next();
    }

    public triggerRefreshCycles(): void {
        this.refreshCyclesSubject.next();
    }

    public triggerRefreshNode(): void {
        this.refreshNodeSubject.next();
    }

    public getLastSelectedNode(): PTreeNodeInterface | null {
        return this.lastSelectedNodeSubject.value;
    }

    public clearLastSelectedNode(): void {
        this.lastSelectedNodeSubject.next(null);
    }

    public doRename(nodeId: number, newName: string): void {
        this.renameSubject.next({ nodeId, newName });
    }

    public deleteFromTree(nodeId: number): void {
        this.deletedSubject.next(nodeId);
    }

    public getNodeRenameByTreeObservable(): Observable<{ nodeId: number; newName: string }> {
        return this.nodeRenamedByTreeSubject.asObservable();
    }

    public getRenameObservable(): Observable<{ nodeId: number; newName: string }> {
        return this.renameSubject.asObservable();
    }

    public getDeletedObservable(): Observable<number> {
        return this.deletedSubject.asObservable();
    }

    public doChangeStatus(nodeId: number, newStatus: string): void {
        this.statusChangedSubject.next({ nodeId, newStatus });
    }

    public getChangeStatusObservable(): Observable<{ nodeId: number; newStatus: string }> {
        return this.statusChangedSubject.asObservable();
    }

    public notifyReady(treeType: TREE_TYPE, tree): void {
        this.treeReadySubject.next({ type: treeType, tree });
    }

    public getTreeReadyObservable(): Observable<{ type: any; tree: any }> {
        return this.treeReadySubject.asObservable();
    }

    // public getActionCompletedObservable(): Observable<unknown> {
    //     return this.actionCompletedSubject.asObservable();
    // }

    public notifyActionCompleted(): void {
        this.actionCompletedSubject.next({});
    }

    public setState(nodeId: number, stateKey, stateValue): void {
        this.stateChangedSubject.next({ nodeId, stateKey, stateValue });
    }

    public getChangeStateObservable(): Subject<{ nodeId: any; stateKey: any; stateValue: any }> {
        return this.stateChangedSubject;
    }

    public renameNodeByTree(nodeId: number, newName: string): void {
        this.nodeRenamedByTreeSubject.next({ nodeId, newName });
    }

    public selectNode(node: PTreeNodeInterface): void {
        this.lastSelectedNodeSubject.next(node);
    }
}

export enum TREE_TYPE {
    MAIN_TREE,
    CREATE_TEST_FROM_REQ,
    IMPORT_FROM_ANOTHER_PROJECT,
    COPY_MOVE_TESTS,
    TEST_CATALOG,
    EDIT_WIDGET_3,
    ADD_TEST_TO_TEST_SET,
    PUSH_OR_GET_TEST_SCRIPT,
    DEFAULT
}
